import { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Send, User, MessageCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function BookingMessages({ bookingId, spotId, ownerName, tenantName, contactMode = false }) {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Determinar endpoint según modo
  const fetchUrl = spotId && !bookingId
    ? `${API}/api/messages/spot/${spotId}`
    : bookingId
    ? `${API}/api/messages/booking/${bookingId}`
    : null;

  const fetchMessages = async () => {
    if (!fetchUrl || !token) return;
    setLoading(true);
    try {
      const res = await fetch(fetchUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 10 seconds
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, spotId, token]);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const body = spotId && !bookingId
        ? { spot_id: spotId, content: newMessage.trim() }
        : { booking_id: bookingId, content: newMessage.trim() };
      const res = await fetch(`${API}/api/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        const sentMessage = await res.json();
        setMessages(prev => [...prev, sentMessage]);
        setNewMessage("");
      } else {
        // Mostrar mensaje del backend (útil p.ej. cuando es una plaza demo)
        try {
          const err = await res.json();
          const msg = err?.detail || "No se ha podido enviar el mensaje";
          if (typeof window !== "undefined") {
            const { toast } = await import("sonner");
            toast?.error?.(msg);
          }
        } catch (_) { /* noop */ }
      }
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    }
    return date.toLocaleDateString("es-ES", { 
      day: "numeric", 
      month: "short",
      hour: "2-digit", 
      minute: "2-digit" 
    });
  };

  return (
    <div className="bg-[#1a1625] border border-white/10 rounded-xl overflow-hidden" data-testid="booking-messages">
      {/* Header */}
      <div className="bg-violet-500/10 border-b border-white/10 p-4">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-violet-400" />
          <h3 className="font-semibold text-white">Mensajes</h3>
          <span className="text-sm text-gray-400">
            Conversa con {ownerName || tenantName || "el otro usuario"}
          </span>
        </div>
      </div>

      {/* Messages area */}
      <div className="h-[280px] overflow-y-auto p-4" ref={scrollRef}>
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <MessageCircle className="w-12 h-12 mb-2 opacity-50" />
            <p>No hay mensajes aún</p>
            <p className="text-sm">¡Inicia la conversación!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => {
              const isOwn = msg.sender_id === user?.id;
              return (
                <div
                  key={msg.id}
                  className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                  data-testid={`message-${msg.id}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      isOwn
                        ? "bg-violet-500 text-white rounded-br-md"
                        : "bg-white/10 text-white rounded-bl-md"
                    }`}
                  >
                    {!isOwn && (
                      <div className="flex items-center gap-1 mb-1">
                        <User className="w-3 h-3 text-violet-400" />
                        <span className="text-xs text-violet-400 font-medium">
                          {msg.sender_name}
                        </span>
                      </div>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-xs mt-1 ${isOwn ? "text-violet-200" : "text-gray-500"}`}>
                      {formatDate(msg.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Input area */}
      <form onSubmit={handleSend} className="border-t border-white/10 p-4">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Escribe un mensaje..."
            className="flex-1 bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
            disabled={sending}
            data-testid="message-input"
          />
          <Button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="bg-violet-500 hover:bg-violet-600 text-white"
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}
